import axiosInstance from '../api';

export type RoomReaction = 'GOOD' | 'CLOSER' | 'CONCERN';
export type OverallSentiment = 'LIKE' | 'DISLIKE';

export interface TourWalkthroughRoom {
  id: string;
  walkthroughId: string;
  roomKey: string;
  reaction: RoomReaction | null;
  note: string | null;
  updatedAt: string;
  uploads?: Array<{
    id: string;
    url: string;
    fileName: string;
  }>;
}

export interface TourWalkthrough {
  id: string;
  showingId: string;
  startedAt: string;
  submittedAt: string | null;
  firstImpression: string | null;
  overallSentiment: OverallSentiment | null;
  overallNote: string | null;
  summaryHeadline: string | null;
  summaryHighlights: string | null;
  summaryRedFlags: string[];
  summaryFollowUps: string[];
  summaryGeneratedAt: string | null;
  /**
   * Whether the recap fields above are readable by the current viewer. The API blanks
   * them for anyone but the buyer who wrote the walkthrough, so a false value means
   * "not yours to see" rather than "not written yet".
   */
  canViewSummary?: boolean;
  rooms: TourWalkthroughRoom[];
  showing?: {
    id: string;
    listingId: string | null;
    listings?: {
      id: string;
      address: string;
      city?: string | null;
      state?: string | null;
    } | null;
  };
}

/** A submitted walkthrough as returned by the buyer's own list of tour recaps. */
export interface TourWalkthroughSummary {
  id: string;
  showingId: string;
  submittedAt: string | null;
  overallSentiment: OverallSentiment | null;
  summaryHeadline: string | null;
  summaryHighlights: string | null;
  summaryRedFlags: string[];
  summaryFollowUps: string[];
  summaryGeneratedAt: string | null;
  showing?: {
    id: string;
    listingId: string | null;
    proposedTime: string;
    listings?: {
      id: string;
      address: string;
      city?: string | null;
      state?: string | null;
    } | null;
  };
}

export type RoomPatch = {
  roomKey: string;
  reaction?: RoomReaction | null;
  note?: string | null;
};

export type SubmitPatch = {
  submit: true;
  overallSentiment?: OverallSentiment | null;
  overallNote?: string | null;
};

export type TopLevelPatch = {
  firstImpression?: string | null;
};

export type TourWalkthroughPatch = RoomPatch | SubmitPatch | TopLevelPatch;

const tourWalkthroughsService = {
  /** The signed-in buyer's own submitted walkthroughs, newest first. */
  find: async (): Promise<TourWalkthroughSummary[]> => {
    const res = await axiosInstance.get<TourWalkthroughSummary[]>(
      '/tour-walkthroughs'
    );
    return res.data;
  },
  get: async (showingId: string): Promise<TourWalkthrough> => {
    const res = await axiosInstance.get<TourWalkthrough>(
      `/tour-walkthroughs/${showingId}`
    );
    return res.data;
  },
  patch: async (
    walkthroughId: string,
    data: TourWalkthroughPatch
  ): Promise<any> => {
    const res = await axiosInstance.patch(
      `/tour-walkthroughs/${walkthroughId}`,
      data
    );
    return res.data;
  },
};

export default tourWalkthroughsService;
